import isEmpty from 'lodash/isEmpty';
import type { GetServerSideProps, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { KEY_ADS_POSITION } from 'src/components/ads/dynamicAds';
import { SHOW_COMMEMORATE } from 'src/constants';
import { IS_NO_ROBOT_INDEX } from 'src/constants/index';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { NAV_DATA } from 'src/redux/navSlice';
import { useAppDispatch, useAppSelector } from 'src/redux/store';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { convertObjPath, escapeChar, timestamp } from 'src/utils/helper';
import { GET } from 'src/utils/service';
import useUser from 'src/utils/useUser';

const Layout = dynamic(() => import('src/components/layouts'));
const SectionOne = dynamic(() => import('src/components/sections/section-home/section-one'), { ssr: true });
const SectionLandingPage = dynamic(() => import('src/components/sections/section-events/section-landing-page'));
const SectionCover = dynamic(() => import('src/components/sections/section-events/section-cover'));
const DynamicAds = dynamic(import('src/components/ads/dynamicAds'));

const PageIndex: NextPage = ({ articles, newHighlightOne, highlightText, dataLandingPage, covers, ads }: any) => {
  const data = useUser();
  const [sessionSevId, setSessionSevId] = useState<any>(null);
  const [sectionMember, setSectionMember] = useState<any>({});
  const [navBar, setNavBar] = useState<any[]>([]);

  useEffect(() => {
    try {
      const checkUser = async () => {
        if (!isEmpty(data) && !isEmpty(data?.user)) {
          const response = await fetch(`/api/auth/metadata`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const res = await response.json();
            localStorage.setItem('login-meta-data', res);
            setSectionMember(decryptionClient(res));
          }
        }
      };

      const loginMetaData = localStorage.getItem('login-meta-data');
      const decryptLoginMetaData = !isEmpty(loginMetaData) ? decryptionClient(loginMetaData) : {};
      const expireCondition: boolean = !isEmpty(loginMetaData) ? new Date(decryptLoginMetaData?.expire) < new Date() : true;

      if (!isEmpty(data) && !isEmpty(data?.user)) {
        setSessionSevId(decryptionClient(data?.user));
        isEmpty(loginMetaData) || expireCondition ? checkUser() : setSectionMember(decryptLoginMetaData);
      }
    } catch (error) {
      console.log('Error: Please enable website cookies. ' + error);
    }
  }, [data]);

  const _article = {
    title: articles?.metaTag?.title || WEB_TITLE,
    keywords: articles?.metaTag?.keywords || WEB_KEYWORD,
    description: articles?.metaTag?.description || WEB_DESC
  };

  const articleDescriptionSeo = escapeChar(_article?.description) || WEB_DESC;
  const navSubMenu = useAppSelector(props => props?.navData?.result?.[0]);
  const dispatch = useAppDispatch();

  // ---------- Fetch menu-nav จากฝั่ง client ----------
  useEffect(() => {
    let mounted = true;
    const fetchMenuNav = async () => {
      try {
        const res = await GET('/api/v1.0/navigations/menu-nav?limit=9');
        if (!mounted || isEmpty(res)) return;

        setNavBar(res);

        const find = res?.filter((i: any) => i.nameEng === 'read');
        if (!isEmpty(find)) {
          dispatch(NAV_DATA(find));
        }
      } catch (err) {
        console.error('fetch menu-nav error:', err);
      }
    };

    fetchMenuNav();
    return () => {
      mounted = false;
    };
  }, [dispatch]);
  return (
    <>
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={_article.title}
        description={_article.description}
        openGraph={{
          type: 'article',
          locale: 'en_US',
          url: WEB_URL.WEB,
          title: _article.title || WEB_TITLE,
          description: _article.description || WEB_DESC,
          images: [
            {
              url: IMAGE_DEFAULT,
              width: 1920,
              height: 1080,
              alt: WEB_NAME
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article.keywords || WEB_KEYWORD
          }
        ]}
      />
      <Head>
        <link rel='canonical' href={WEB_URL.WEB} />
        <meta property='twitter:title' content={_article?.title || WEB_TITLE} />
        <meta property='twitter:description' content={_article?.description || WEB_DESC} />
        <meta property='twitter:image' content={IMAGE_DEFAULT} />

        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                __th_page="home";
              `
          }}
        />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                  {
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": "${WEB_NAME}",
                    "url": "${WEB_URL.WEB}",
                    "description": "${articleDescriptionSeo}",
                    "sameAs": [
                      "${WEB_URL.FACEBOOK}",
                      "${WEB_URL.YOUTUBE}",
                      "${WEB_URL.INSTAGRAM}",
                      "${WEB_URL.TWITTER}"
                    ],
                    "potentialAction": {
                      "@type": "SearchAction",
                      "target": "${WEB_URL.WEB}search?q={search_term_string}",
                      "query-input": "required name=search_term_string"
                    }
                  }
                }
              `
          }}
        />

        {/* ---------------------------- SPIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                  {
                    "@context": "http://schema.org",
                    "@type": "WebPage",
                    "name": "${WEB_NAME}",
                    "description": "${articleDescriptionSeo}",
                    "publisher": {
                      "@type": "ProfilePage",
                      "name": "${WEB_NAME}"
                    }
                  }
                `
          }}
        />
      </Head>

      <SectionLandingPage data={dataLandingPage} />
      {!isEmpty(covers) && <SectionCover data={covers} />}

      <Layout data={{ header: navBar, footer: navBar }} sessionSevId={sessionSevId} newHighlightOne={newHighlightOne} isHomePage={true}>
        <div
          style={{
            filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset'
          }}
        >
          <SectionOne data={articles} highlightText={highlightText} navBar={navBar} subMenu={navSubMenu} ads={ads} sectionMember={sectionMember} />
          {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.AD1X1} data={ads} />}
        </div>
      </Layout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx: any) => {
  ctx.res.setHeader('Cache-Control', 'public, s-maxage=120, stale-while-revalidate=600');

  const initProps: any = {
    statusCode: 200,
    errMessage: null,
    articles: {},
    covers: [],
    dataLandingPage: [],
    newHighlightOne: null,
    highlightText: [],
    ads: [],
    user: {}
  };

  const payload = {
    block1: 8,
    block2: 7,
    block3: 8,
    headerLatestNews: 5
  };

  try {
    const [fetchImagesCover, fetchSectionOne, fetchLandingPage] = await Promise.all([
      GET('/api/v1.0/imagecover'),
      GET(`/api/v1.0/home/section-one${convertObjPath(payload)}`),
      GET('/api/landing-page/list')
    ]);

    if (!isEmpty(fetchSectionOne)) {
      initProps.articles = fetchSectionOne;
      initProps.ads = fetchSectionOne?.ads;
      initProps.newHighlightOne = fetchSectionOne?.newHighlight;
    }

    if (!isEmpty(fetchImagesCover)) initProps.covers = fetchImagesCover;
    if (!isEmpty(fetchLandingPage)) initProps.dataLandingPage = fetchLandingPage;
  } catch (err: any) {
    console.error(`${timestamp()} ==========> PAGE_INDEX ERROR : `, err.message);
    initProps.errMessage = err.message;
    initProps.statusCode = err?.response?.status || 500;
  }

  return {
    props: initProps
  };
};

export default PageIndex;
